import axios from "axios";
import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import AlertComponent from "../../components/AlertComponent";

function PageShell({ children }) {
  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10 relative overflow-hidden"
      style={{ background: "linear-gradient(160deg, #1e1b4b 0%, #3b0764 100%)" }}
    >
      <div
        className="absolute -top-32 -left-32 w-96 h-96 rounded-full opacity-30 blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle,#a78bfa,transparent)" }}
      />
      <div
        className="absolute -bottom-40 -right-24 w-[28rem] h-[28rem] rounded-full opacity-20 blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle,#c4b5fd,transparent)" }}
      />
      <div
        className="absolute top-1/3 right-1/4 w-72 h-72 rounded-full opacity-10 blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle,#8b5cf6,transparent)" }}
      />

      <div className="w-full max-w-md relative">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-900/40"
            style={{ background: "linear-gradient(135deg,#8b5cf6,#6d28d9)" }}
          >
            <svg className="text-white w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <span className="text-white font-bold text-lg tracking-wide">San Mateo Deployer</span>
        </div>

        {children}

        <p className="mt-6 text-center text-xs text-purple-300/70">
          © {new Date().getFullYear()} San Mateo Deployer · Todos os direitos reservados
        </p>
      </div>
    </div>
  );
}

export default function PrimeiroAcesso() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token");
  const email = searchParams.get("email");

  const [senha, setSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [showSenha, setShowSenha] = useState(false);
  const [showConfirmar, setShowConfirmar] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const linkInvalido = !token || !email;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!senha || senha.length < 6) {
      AlertComponent("Erro", "A senha deve ter pelo menos 6 caracteres", { icon: "error" });
      return;
    }
    if (senha !== confirmarSenha) {
      AlertComponent("Erro", "As senhas não coincidem", { icon: "error" });
      return;
    }

    setSalvando(true);
    try {
      await axios.post(`${process.env.REACT_APP_API}/auth/primeiro-acesso`, {
        email,
        token,
        newPassword: senha,
      });

      AlertComponent("Sucesso", "Senha definida com sucesso! Faça login para continuar.", { icon: "success" });
      navigate("/login");
    } catch (err) {
      const mensagem = err?.response?.data?.message || err?.message || "Erro ao definir senha";
      AlertComponent("Erro", mensagem, { icon: "error" });
    } finally {
      setSalvando(false);
    }
  };

  if (linkInvalido) {
    return (
      <PageShell>
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl shadow-purple-950/40 ring-1 ring-white/20 p-7 sm:p-9">
          <div className="flex justify-center mb-5">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-red-50 ring-1 ring-red-100">
              <svg className="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M4.93 19h14.14a2 2 0 001.74-3l-7.07-12a2 2 0 00-3.48 0l-7.07 12a2 2 0 001.74 3z" />
              </svg>
            </div>
          </div>

          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-slate-900 mb-1">Link inválido</h1>
            <p className="text-sm text-slate-500">
              Este link de primeiro acesso é inválido ou está incompleto.
            </p>
          </div>

          <Link
            to="/login"
            className="block w-full text-center py-3 rounded-xl font-semibold text-sm tracking-wide text-white shadow-lg shadow-purple-900/30 hover:shadow-purple-900/50 transition-all duration-200 hover:-translate-y-0.5"
            style={{ background: "linear-gradient(135deg,#7c3aed,#6d28d9)" }}
          >
            Ir para login
          </Link>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl shadow-purple-950/40 ring-1 ring-white/20 p-7 sm:p-9">
        <div className="flex justify-center mb-5">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center"
            style={{ background: "linear-gradient(135deg,#ede9fe,#f5f3ff)" }}
          >
            <svg className="w-7 h-7 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
          </div>
        </div>

        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-1">Definir senha</h1>
          <p className="text-sm text-slate-500">
            Bem-vindo! Crie uma senha para acessar o sistema.
          </p>
          <p className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-violet-50 ring-1 ring-violet-100 text-[11px] font-medium text-violet-700 max-w-full">
            <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="truncate">{email}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <PasswordInput
            label="Nova senha"
            placeholder="Mínimo 6 caracteres"
            value={senha}
            onChange={setSenha}
            show={showSenha}
            onToggleShow={() => setShowSenha((s) => !s)}
            disabled={salvando}
            autoFocus
          />

          <PasswordInput
            label="Confirmar senha"
            placeholder="Repita a senha"
            value={confirmarSenha}
            onChange={setConfirmarSenha}
            show={showConfirmar}
            onToggleShow={() => setShowConfirmar((s) => !s)}
            disabled={salvando}
          />

          <button
            type="submit"
            disabled={salvando}
            className="w-full text-white py-3 rounded-xl font-semibold text-sm tracking-wide
                       shadow-lg shadow-purple-900/30 hover:shadow-purple-900/50
                       transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed
                       hover:-translate-y-0.5 active:translate-y-0"
            style={{ background: "linear-gradient(135deg,#7c3aed,#6d28d9)" }}
          >
            {salvando ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
                Salvando...
              </span>
            ) : (
              "Definir senha"
            )}
          </button>
        </form>

        <div className="mt-6 flex justify-center">
          <Link
            to="/login"
            className="flex items-center gap-1.5 text-xs font-medium text-violet-600 hover:text-violet-800 transition"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Voltar para login
          </Link>
        </div>
      </div>
    </PageShell>
  );
}

function PasswordInput({ label, placeholder, value, onChange, show, onToggleShow, disabled, autoFocus }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
        {label}
      </label>
      <div className="relative">
        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </span>
        <input
          type={show ? "text" : "password"}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-xl border border-slate-200 bg-slate-50/50 pl-10 pr-10 py-2.5 text-sm text-slate-900 placeholder-slate-400
                     focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent focus:bg-white transition"
          required
          minLength={6}
          disabled={disabled}
          autoFocus={autoFocus}
        />
        <button
          type="button"
          onClick={onToggleShow}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-violet-600 transition"
          tabIndex={-1}
          aria-label={show ? "Ocultar senha" : "Mostrar senha"}
        >
          {show ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}
